import { useMemo } from 'react';
import { useSLTConfig } from '../../slt/config/SLTConfigProvider';
import { useCustomPreferences } from '../../utils/api';
import { FEATURE_PREFERENCE_MAP } from '../recaptcha';
import type {
    RecaptchaEnterpriseConfig,
    RecaptchaFeature,
    RecaptchaFeatureConfig,
} from '../recaptcha';

export interface UseRecaptchaFeatureResult {
    config: RecaptchaFeatureConfig | null;
    enterpriseConfig: RecaptchaEnterpriseConfig | null;
    isEnabled: boolean;
    error: string | null;
}

export const useRecaptchaFeature = (feature: RecaptchaFeature): UseRecaptchaFeatureResult => {
    const sltConfig = useSLTConfig();
    const preferences = useCustomPreferences([]);

    const result = useMemo(() => {
        const enterpriseConfig: RecaptchaEnterpriseConfig | null =
            sltConfig.env.recaptchaEnterpriseProjectId &&
            sltConfig.env.recaptchaEnterpriseApiKey &&
            sltConfig.env.recaptchaEnterpriseV3SiteKey &&
            sltConfig.env.recaptchaEnterpriseV2SiteKey
                ? {
                      projectId: sltConfig.env.recaptchaEnterpriseProjectId,
                      apiKey: sltConfig.env.recaptchaEnterpriseApiKey,
                      siteKeyV3: sltConfig.env.recaptchaEnterpriseV3SiteKey,
                      siteKeyV2: sltConfig.env.recaptchaEnterpriseV2SiteKey,
                  }
                : null;

        if (!enterpriseConfig) {
            return {
                config: null,
                enterpriseConfig: null,
                isEnabled: false,
                error: 'reCAPTCHA Enterprise configuration is incomplete',
            };
        }

        const featureMap = FEATURE_PREFERENCE_MAP[feature];
        if (!featureMap) {
            return {
                config: null,
                enterpriseConfig,
                isEnabled: false,
                error: `Unknown reCAPTCHA feature: ${feature}`,
            };
        }

        const enabledValue = preferences[featureMap.enabledPref];
        const enableV2FallbackValue = preferences[featureMap.v2FallbackPref];

        const enabled = enabledValue === 'true' || enabledValue === true;
        const enableV2Fallback = enableV2FallbackValue === 'true' || enableV2FallbackValue === true;

        const config: RecaptchaFeatureConfig = {
            enabled,
            enableV2Fallback,
        };

        return {
            config,
            enterpriseConfig,
            isEnabled: enabled,
            error: null,
        };
    }, [feature, sltConfig.env, preferences]);

    return result;
};
